<template>
  <div
    class="tw-bg-[#FCFCFC] tw-h-[119px] tw-w-[100%] tw-border tw-border-solid tw-border-[#EFF0F1] tw-rounded-[6px]"
  >
    <div class="tw-flex tw-h-full tw-justify-between">
      <div class="tw-flex align-items-end tw-p-5 tw-w-full">
        <div class="detail-info">
          <div
            class="tw-text-[#211F58] tw-text-[13px] tw-font-[500] tw-leading-[130%] tw-not-italic"
          >
            <div
              class="tw-flex tw-justify-between tw-items-center tw-text-[15px] tw-font-[600] tw-leading-[130%] tw-not-italic"
            >
              {{
                getComponentUiText(
                  "app-component-assessments-results-card-title-1",
                )
              }}
            </div>
            <div
              class="tw-flex tw-justify-between tw-items-center tw-font-[500] tw-my-[11px]"
            >
              {{
                getComponentUiText(
                  "app-component-assessments-results-passed-review-title-1",
                )
              }}
              <div
                v-if="isLoading"
                class="custom-global-skeleton tw-w-[25%] tw-h-[16px]"
              ></div>
              <div v-else class="tw-font-[400]">
                <span class="tw-pr-1 tw-text-[#B7B7B7]"
                  >{{ data.evaluated.completed }}/{{
                    data.evaluated.total
                  }}</span
                >
                <span class="tw-text-[#878787]"
                  >{{ data.evaluated.percent }}%</span
                >
              </div>
            </div>
            <div
              class="tw-flex tw-justify-between tw-items-center tw-font-[500]"
            >
              {{
                getComponentUiText(
                  "app-component-assessments-status-card-score-1",
                )
              }}
              <div
                v-if="isLoading"
                class="custom-global-skeleton tw-w-[25%] tw-h-[16px]"
              ></div>
              <div v-else class="tw-flex tw-items-center tw-gap-[15px]">
                <div class="tw-font-[400]">
                  <span class="tw-pr-1 tw-text-[#B7B7B7]"
                    >{{ data.results.completed }}/{{ data.results.total }}</span
                  >
                  <span class="tw-text-[#878787]"
                    >{{ data.results.percent }}%</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div
          v-if="isLoading"
          class="custom-global-skeleton tw-w-[113px] tw-h-[117px] tw-border-l tw-border-[0] tw-border-solid tw-border-[#EFF0F1]"
        ></div>
        <div
          v-else-if="data.results.percent && status !== CHECKLISTSTATUS.DONE"
          class="tw-px-[5px] sm:tw-p-[20px] tw-border-l tw-border-solid tw-border-[#EFF0F1] tw-h-full tw-border-[0] tw-flex tw-justify-center tw-items-center tw-min-w-[80px]"
        >
          <RadialProgressBar
            class="d-inline"
            :total-steps="100"
            :completed-steps="data.results.percent"
            :is-hide-progress="true"
            outer-stroke-color="#48A0FF"
          />
        </div>
        <div
          v-else-if="
            data.results.percent &&
            status === CHECKLISTSTATUS.DONE &&
            isVendorAssessment
          "
          :class="[
            'tw-flex tw-items-center tw-justify-center tw-pt-[55px] tw-p-[20px] tw-bg-[#ffffff] tw-max-w-[113px] tw-max-h-[117px] tw-rounded-r-md tw-border-l tw-border-[0] tw-border-solid tw-border-[#EFF0F1] tw-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.05)]',
          ]"
        >
          <VendorAssessmentScoreChart
            :current-score="data.results.percent / 100"
            :pcnt-score="data.results.percent / 100"
            :note-color="'#62BB47'"
            :current-score-color="
              Number(data.results.percent / 100) === 0 ? '#F7553C' : '#01893D'
            "
            :total-score-color="
              Number(data.results.percent / 100) === 0 ? '#FEE5E2' : '#D9EDE2'
            "
            :width="155"
          />
        </div>
        <div
          v-else
          :class="[
            'tw-flex tw-items-center tw-justify-center tw-p-[20px] tw-bg-[#ffffff] tw-max-w-[113px] tw-max-h-[117px] tw-rounded-r-md tw-border-l tw-border-[0] tw-border-solid tw-border-[#EFF0F1] tw-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.05)]',
          ]"
        >
          <div
            class="tw-w-[113px] tw-h-[117px] tw-flex tw-items-center tw-justify-center tw-text-[30px] tw-font-[600]"
            :class="`tw-text-[${
              TEXT_COLOR_WITH_GRADE_SCORE[
                generateGradeScoreWithCompletedScore(
                  Number(data.results.percent),
                )
              ]
            }]`"
          >
            {{
              generateGradeScoreWithCompletedScore(Number(data.results.percent))
            }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  generateGradeScoreWithCompletedScore,
  TEXT_COLOR_WITH_GRADE_SCORE,
} from "@/services/assessment";
import { CHECKLISTSTATUS } from "@/utils/constants";
import { getComponentUiText } from "@/utils/utilities";
import RadialProgressBar from "../prospect/prospectQuestions/componentTypes/radialProgressBar.vue";
import CyberHealthScoreChart from "../chart/cyberHealthScoreChart.vue";
import VendorAssessmentScoreChart from "../vendor/vendorAssessmentScoreChart.vue";

interface Evaluated {
  completed: number;
  total: number;
  percent: number;
}

interface Results {
  completed: number;
  total: number;
  percent: number;
}

interface Data {
  evaluated: Evaluated;
  results: Results;
}

defineProps<{
  data: Data;
  isLoading: boolean;
  status: CHECKLISTSTATUS;
  isVendorAssessment?: boolean;
}>();
</script>

<style scoped>
.detail-info {
  flex: 1;
  font-family: Poppins;
}

.result-status {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  opacity: 0.5;
}
.result-status-number {
  font-size: 13px;
  font-weight: 500;
  color: #878787;
}
</style>
